import type { ReactNode } from "react";
import {
  Badge,
  Button,
  DataTable,
  SplitButton,
  type Column
} from "../../components/index.js";
import type { PlanSummary } from "../../types.js";
import { planBadge } from "../../lib/plan-status.js";
import {
  deriveDestinationLabel,
  formatDateTime,
  referenceSuffix
} from "../../lib/shipments-view.js";

/**
 * Tabela de remessas (Design.md §5.4). Colunas na ordem normativa, TODAS reais:
 * Nome/ID, Status, Criado em, Última atualização, Enviar para, SKU (nº de MSKUs
 * distintos) e Unidades (soma das quantidades) — `skuCount`/`totalUnits` vêm do
 * GET /inbound-plans. Ações: Visualizar (abre detalhe) / Enviar novamente
 * (planos cancelados → Criar envios).
 */
export interface ShipmentsTableProps {
  plans: readonly PlanSummary[];
  loading: boolean;
  onView: (id: string) => void;
  onResend: (plan: PlanSummary) => void;
}

export function ShipmentsTable({
  plans,
  loading,
  onView,
  onResend
}: ShipmentsTableProps): ReactNode {
  const columns: Column<PlanSummary>[] = [
    {
      key: "name",
      header: "Nome da remessa",
      render: (plan) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
          <button
            type="button"
            onClick={() => onView(plan.id)}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              textAlign: "left",
              color: "var(--blue)",
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            <span aria-hidden={true} style={{ marginRight: "0.35rem" }}>
              🇬🇧
            </span>
            {plan.name ?? "(sem nome)"}
          </button>
          <span className="mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
            {plan.id}
            {referenceSuffix(plan.spApiInboundPlanId, plan.id)}
          </span>
        </div>
      )
    },
    {
      key: "status",
      header: "Status",
      render: (plan) => {
        const badge = planBadge(plan.status);
        return <Badge tone={badge.tone}>{badge.label}</Badge>;
      }
    },
    {
      key: "createdAt",
      header: "Criado em",
      render: (plan) => formatDateTime(plan.createdAt)
    },
    {
      key: "updatedAt",
      header: "Última atualização",
      render: (plan) => formatDateTime(plan.updatedAt)
    },
    {
      key: "destination",
      header: "Enviar para",
      render: (plan) => deriveDestinationLabel(plan.destinationMarketplaces)
    },
    {
      key: "sku",
      header: "SKU",
      align: "right",
      render: (plan) =>
        plan.skuCount !== undefined ? (
          plan.skuCount.toLocaleString("pt-BR")
        ) : (
          <span style={{ color: "var(--text-faint)" }}>—</span>
        )
    },
    {
      key: "units",
      header: "Unidades",
      align: "right",
      render: (plan) =>
        plan.totalUnits !== undefined ? (
          plan.totalUnits.toLocaleString("pt-BR")
        ) : (
          <span style={{ color: "var(--text-faint)" }}>—</span>
        )
    },
    {
      key: "actions",
      header: "Ações",
      align: "right",
      render: (plan) =>
        plan.status === "CANCELLED" ? (
          <SplitButton
            label="Enviar novamente"
            variant="primary"
            onClick={() => onResend(plan)}
            items={[{ label: "Visualizar", onClick: () => onView(plan.id) }]}
          />
        ) : (
          <Button variant="secondary" onClick={() => onView(plan.id)}>
            Visualizar
          </Button>
        )
    }
  ];

  return (
    <DataTable
      columns={columns}
      rows={plans}
      getRowKey={(plan) => plan.id}
      empty={loading ? "Carregando remessas…" : "Nenhuma remessa encontrada."}
    />
  );
}
